import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { ArrowRight, MapPin, CalendarCheck, LayoutDashboard, CreditCard, Search, Tag, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";

const CONTENT_DEFAULTS = {
  announcement: "",
  hero_eyebrow: "The reservation platform for remarkable dining",
  hero_title: "Every great evening begins with a table.",
  hero_subtitle: "TableDiary connects diners with the city's best tables — and gives restaurants the tools to fill them.",
  hero_image: "https://images.unsplash.com/photo-1653259038915-7cf0b7a4dd6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwcmVzdGF1cmFudCUyMGludGVyaW9yJTIwZXZlbmluZ3xlbnwwfHx8fDE3ODc1NTc4NzB8MA&ixlib=rb-4.1.0&q=85",
};

const FALLBACK_IMG = "https://images.pexels.com/photos/30003229/pexels-photo-30003229.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AREA_IMAGES = [
  "https://images.unsplash.com/photo-1677126369481-81b4ddcf0e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxjaXR5JTIwbmVpZ2hib3Job29kJTIwc3RyZWV0fGVufDB8fHx8MTc4NzkzNDQ5Mnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1627927518258-b67557570840?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxjaXR5JTIwbmVpZ2hib3Job29kJTIwc3RyZWV0fGVufDB8fHx8MTc4NzkzNDQ5Mnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1641465537505-65fb9572571d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxtYW5jaGVzdGVyJTIwY2l0eSUyMHN0cmVldCUyMGV2ZW5pbmd8ZW58MHx8fDE3ODc5MzQ4ODR8MA&ixlib=rb-4.1.0&q=85",
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export default function Landing() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [allRests, setAllRests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [content, setContent] = useState(CONTENT_DEFAULTS);
  const vis = (k) => content.sections?.[k] !== false;
  const [locations, setLocations] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [tonight, setTonight] = useState([]);
  const [nearCity, setNearCity] = useState("");
  const [sq, setSq] = useState("");
  const [sc, setSc] = useState("");
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    api.get("/restaurants").then((r) => {
      setFeatured(r.data.slice(0, 3));
      setAllRests(r.data);
    }).catch(() => {});
    api.get("/offers").then((r) => setOffers(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent((c) => ({ ...c, ...r.data.landing }))).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data.filter((l) => l.active !== false))).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`);
          const geo = await res.json();
          const city = geo.address?.city || geo.address?.town || geo.address?.village || geo.address?.county;
          if (city) {
            const r = await api.get("/restaurants", { params: { city } });
            if (r.data.length) {
              setNearCity(city);
              setNearby(r.data.slice(0, 3));
              api.get("/restaurants/tonight", { params: { city } }).then((t) => setTonight(t.data)).catch(() => {});
            }
          }
        } catch (e) { /* ignore */ }
      }, () => {});
    }
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const cityCounts = useMemo(() => {
    const m = {};
    for (const r of allRests) {
      const k = (r.city || "").toLowerCase();
      if (k) m[k] = (m[k] || 0) + 1;
    }
    return m;
  }, [allRests]);

  const goSearch = () => {
    const params = new URLSearchParams();
    if (sq.trim()) params.set("q", sq.trim());
    if (sc.trim()) params.set("city", sc.trim());
    navigate(`/restaurants${params.toString() ? `?${params}` : ""}`);
  };

  return (
    <div data-testid="landing-page" className="bg-background">
      {content.announcement && (
        <div data-testid="announcement-bar" className="bg-primary text-primary-foreground text-center text-xs tracking-[0.15em] uppercase py-2.5 px-4">
          {content.announcement}
        </div>
      )}
      {vis("hero") && (
      <section className="dark relative grain min-h-[92vh] flex items-end overflow-hidden">
        <img
          src={content.hero_image}
          alt="Elegant restaurant interior"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-black/50 to-black/20" />
        <div className="relative max-w-7xl mx-auto px-6 pb-24 pt-40 w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-xs tracking-[0.3em] uppercase text-primary mb-6 [text-shadow:0_2px_12px_rgba(0,0,0,0.65)]">
            {content.hero_eyebrow}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none max-w-3xl text-foreground">
            {content.hero_title}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="mt-6 text-lg text-white/80 max-w-xl">
            {content.hero_subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
            className="mt-10 max-w-2xl">
            <div className="flex items-center gap-1 rounded-full bg-[#FAF9F6]/95 backdrop-blur-xl p-1.5 shadow-[0_16px_48px_rgba(0,0,0,0.25)] border border-white/10">
              <Input data-testid="search-name-input" placeholder="Restaurant or cuisine..." value={sq}
                onChange={(e) => setSq(e.target.value)} onKeyDown={(e) => e.key === "Enter" && goSearch()}
                className="border-0 bg-transparent focus-visible:ring-0 text-[#1A1A1A] placeholder:text-[#1A1A1A]/45 h-11" />
              <div className="w-px h-6 bg-[#1A1A1A]/15 shrink-0 hidden sm:block" />
              <Input data-testid="search-city-input" placeholder="Area or city" value={sc}
                onChange={(e) => setSc(e.target.value)} onKeyDown={(e) => e.key === "Enter" && goSearch()}
                className="border-0 bg-transparent focus-visible:ring-0 text-[#1A1A1A] placeholder:text-[#1A1A1A]/45 h-11 hidden sm:block sm:max-w-[150px]" />
              <Button data-testid="search-btn" onClick={goSearch} className="rounded-full px-6 h-11 shrink-0 hover:-translate-y-0 transition-transform duration-200">
                <Search className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Search</span>
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }} className="mt-6 flex flex-wrap gap-4">
            <Link to="/restaurants">
              <Button data-testid="hero-find-table-btn" size="lg" variant="outline" className="rounded-full px-8 bg-black/30 backdrop-blur text-white border-white/40 hover:bg-white/10 hover:text-white">
                Browse all tables <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/register?role=owner">
              <Button data-testid="hero-vendor-btn" size="lg" variant="ghost" className="rounded-full px-8 text-white/80 hover:text-white hover:bg-white/10">
                For restaurants
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
      )}

      {locations.length > 0 && vis("cities") && (
        <section className="max-w-7xl mx-auto px-6 pt-24" data-testid="cities-section">
          <motion.p {...fadeUp} className="text-xs tracking-[0.3em] uppercase text-primary mb-4">Discover by area</motion.p>
          <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl tracking-tight mb-12">
            Where to tonight?
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {locations.map((l, i) => (
              <motion.div key={l.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}>
                <Link to={`/c/${l.slug}`} data-testid={`city-link-${l.slug}`}
                  className="group block relative overflow-hidden rounded-2xl border border-border/60 aspect-[4/5] hover:-translate-y-[3px] transition-transform duration-300">
                  <img src={AREA_IMAGES[i % AREA_IMAGES.length]} alt={l.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 p-5">
                    <h3 className="font-display text-xl text-white">{l.name}</h3>
                    <p className="text-xs text-white/70 mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {cityCounts[l.name.toLowerCase()] || 0} restaurant{(cityCounts[l.name.toLowerCase()] || 0) === 1 ? "" : "s"}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {tonight.length > 0 && vis("tonight") && (
        <section className="max-w-7xl mx-auto px-6 pt-24" data-testid="tonight-section">
          <motion.p {...fadeUp} className="text-xs tracking-[0.3em] uppercase text-primary mb-4">Tonight near you</motion.p>
          <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl tracking-tight mb-10">
            Still free this evening
          </motion.h2>
          <div className="flex gap-5 overflow-x-auto pb-4 -mx-6 px-6">
            {tonight.map((r, i) => (
              <motion.div key={r.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="shrink-0">
                <Link to={`/restaurants/${r.slug || r.id}`} data-testid={`tonight-${r.id}`}
                  className="block w-[260px] rounded-2xl border border-border/60 bg-card overflow-hidden hover:-translate-y-[3px] transition-transform duration-300">
                  <img src={r.image_url || FALLBACK_IMG} alt={r.name} className="w-full h-36 object-cover" />
                  <div className="p-5">
                    <p className="text-xs tracking-[0.2em] uppercase text-primary">{r.cuisine}</p>
                    <h3 className="font-display text-lg mt-1">{r.name}</h3>
                    <p className="text-sm text-primary mt-2">Next table {r.next_slot} · {r.slots_left} slot{r.slots_left === 1 ? "" : "s"} left</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {nearby.length > 0 && vis("nearby") && (
        <section className="max-w-7xl mx-auto px-6 pt-24" data-testid="nearby-section">
          <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl tracking-tight mb-10 flex items-center gap-3">
            <MapPin className="w-6 h-6 text-primary" /> Near you in {nearCity}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {nearby.map((r) => (
              <Link key={r.id} to={`/restaurants/${r.slug || r.id}`} data-testid={`nearby-${r.id}`} className="group block">
                <div className="rounded-2xl border border-border/60 bg-card overflow-hidden hover:-translate-y-[3px] transition-transform duration-300">
                  <img src={r.image_url || FALLBACK_IMG}
                    alt={r.name} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="p-5">
                    <p className="text-xs tracking-[0.2em] uppercase text-primary">{r.cuisine}</p>
                    <h3 className="font-display text-xl mt-1">{r.name}</h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {vis("featured") && (
      <section className="max-w-7xl mx-auto px-6 py-32">
        <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl tracking-tight mb-14">
          This week's tables
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {featured.map((r, i) => (
            <motion.div key={r.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.12 }}
              className={i === 0 ? "md:col-span-8 md:row-span-2" : "md:col-span-4"}>
              <Link to={`/restaurants/${r.slug || r.id}`} data-testid={`featured-restaurant-${r.id}`} className="group block h-full">
                <div className="relative overflow-hidden rounded-2xl border border-border/60 h-full">
                  <img src={r.image_url || FALLBACK_IMG}
                    alt={r.name}
                    className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${i === 0 ? "h-72 md:h-full md:min-h-[37rem]" : "h-72"}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <p className="text-xs tracking-[0.2em] uppercase text-[#F0B9A2] [text-shadow:0_1px_8px_rgba(0,0,0,0.8)]">{r.cuisine}</p>
                    <h3 className={`font-display mt-1 text-white ${i === 0 ? "text-3xl" : "text-2xl"}`}>{r.name}</h3>
                    <p className="text-sm text-white/70 flex items-center gap-1 mt-1">
                      <MapPin className="w-3.5 h-3.5" /> {r.city}
                    </p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
          {featured.length === 0 && (
            <p className="text-muted-foreground md:col-span-12">Restaurants are on their way.</p>
          )}
        </div>
      </section>
      )}

      {plans && vis("pricing") && (
        <section className="border-t border-border/50" data-testid="pricing-section">
          <div className="max-w-7xl mx-auto px-6 py-24">
            <motion.p {...fadeUp} className="text-xs tracking-[0.3em] uppercase text-primary mb-4">For restaurants</motion.p>
            <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl tracking-tight mb-3">
              Simple pricing that pays for itself
            </motion.h2>
            <motion.p {...fadeUp} className="text-muted-foreground mb-12 max-w-2xl">
              Start free, upgrade when you're full. Every plan includes the service calendar, live floor plan and guest CRM.
            </motion.p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(plans).sort((a, b) => (a[1].price || 0) - (b[1].price || 0)).map(([tier, p], i) => (
                <motion.div key={tier} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                  data-testid={`pricing-card-${tier}`}
                  className={`rounded-2xl border p-8 flex flex-col ${tier === "pro" ? "border-primary bg-primary/5 shadow-lg" : "border-border/60 bg-card"}`}>
                  {tier === "pro" && <p className="text-xs tracking-[0.2em] uppercase text-primary mb-2">Most popular</p>}
                  <h3 className="font-display text-2xl">{p.name}</h3>
                  <p className="mt-3">
                    <span className="font-display text-4xl">£{p.price}</span>
                    <span className="text-muted-foreground text-sm">/month</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">+ £{Number(p.commission).toFixed(2)} per booking</p>
                  <ul className="mt-6 space-y-2.5 text-sm flex-1">
                    {(p.features || []).map((f, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />{f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/register" className="mt-8 block">
                    <Button data-testid={`pricing-cta-${tier}`} variant={tier === "pro" ? "default" : "outline"} className="w-full rounded-full">
                      {p.price > 0 ? `Start with ${p.name}` : "Start free"}
                    </Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {offers.length > 0 && vis("offers") && (
        <section className="border-t border-border/50" data-testid="offers-strip">
          <div className="max-w-7xl mx-auto px-6 py-24">
            <motion.p {...fadeUp} className="text-xs tracking-[0.3em] uppercase text-primary mb-4 flex items-center gap-2">
              <Tag className="w-4 h-4" /> Special offers
            </motion.p>
            <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl tracking-tight mb-12">
              Worth booking for
            </motion.h2>
            <div className="flex gap-5 overflow-x-auto pb-4 -mx-6 px-6">
              {offers.map((o, i) => (
                <motion.div key={o.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="shrink-0">
                  <Link to={`/restaurants/${o.restaurant_slug || o.restaurant_id}`} data-testid={`offer-card-${o.id}`}
                    className="block w-[300px] rounded-2xl border border-primary/30 bg-primary/5 p-6 hover:-translate-y-[2px] transition-transform duration-300">
                    <p className="text-xs tracking-[0.2em] uppercase text-primary">{o.restaurant_name}</p>
                    <h3 className="font-outfit font-medium text-lg mt-2">{o.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{o.description}</p>
                    {o.valid_until && <p className="text-xs text-muted-foreground mt-4">Valid until {o.valid_until}</p>}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {vis("cta") && (
      <section className="border-t border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="md:col-span-5">
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Run your floor like a maison.</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Real-time availability, drag-and-drop floor plans, staff roles, and billing that scales with your covers.
            </p>
            <Link to="/register?role=owner">
              <Button data-testid="cta-vendor-btn" className="mt-8 rounded-full px-8">
                Start free <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: CalendarCheck, title: "Smart availability", text: "No double-bookings. Turnover-aware slots matched to party size and table capacity." },
              { icon: LayoutDashboard, title: "Live floor plan", text: "Drag tables, assign seats, and track every cover from confirmed to completed." },
              { icon: CreditCard, title: "Plans that flex", text: "Start free. Upgrade to Starter or Pro as your dining room grows." },
            ].map((f, i) => (
              <motion.div key={f.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="rounded-2xl border border-border/60 bg-card p-6 hover:-translate-y-[2px] transition-transform duration-300">
                <f.icon className="w-6 h-6 text-primary" />
                <h3 className="font-outfit font-medium text-lg mt-4">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.text}</p>
              </motion.div>
            ))}
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }}
              className="rounded-2xl overflow-hidden border border-border/60 relative">
              <img src="https://images.pexels.com/photos/36430149/pexels-photo-36430149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Chef plating" className="w-full h-full object-cover min-h-[180px]" />
            </motion.div>
          </div>
        </div>
      </section>
      )}

      <footer className="border-t border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col sm:flex-row justify-between gap-4 text-sm text-muted-foreground">
          <span className="font-display text-foreground text-lg">TableDiary</span>
          <span className="flex items-center gap-4">
            <Link to="/demo" data-testid="footer-demo-link" className="text-primary hover:underline">Live demo</Link>
            <span>Reserve beautifully. Host brilliantly.</span>
          </span>
        </div>
      </footer>
    </div>
  );
}
